import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import styles from './CreateDocModal.module.scss'

/**
 * Searchable dropdown with portal-based menu (no overflow clipping issues).
 * items: [{ value, label, sub1?, sub2? }]
 */
export default function SearchPicker({
  items = [],
  value,
  onChange,
  placeholder = tr("Поиск…"),
  label,
  error,
  required,
  emptyLabel = tr("— Не выбрано —"),
}) {
  useInterfaceLanguage();
  const [open, setOpen]       = useState(false)
  const [query, setQuery]     = useState('')
  const [dropRect, setDropRect] = useState(null)
  const wrapRef    = useRef(null)
  const triggerRef = useRef(null)
  const inputRef   = useRef(null)

  const selected = value != null ? items.find(i => String(i.value) === String(value)) : null

  /* Close on outside click */
  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  /* Track trigger position for portal dropdown */
  useEffect(() => {
    if (!open || !triggerRef.current) return
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (r) setDropRect({ top: r.bottom + 6, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  const filtered = items.filter(i => {
    if (!query) return true
    const q = query.toLowerCase()
    return [i.label, i.sub1, i.sub2].some(text => typeof text === 'string' && (
      text.toLowerCase().includes(q) || tr(text).toLowerCase().includes(q)
    ))
  }).slice(0, 14)

  function handleOpen() {
    setOpen(true); setQuery('')
    setTimeout(() => inputRef.current?.focus(), 30)
  }

  function handleSelect(item) {
    onChange(item); setOpen(false); setQuery('')
  }

  function handleClear(e) {
    e.stopPropagation(); onChange(null)
  }

  /* Portal dropdown rendered directly on document.body */
  const dropdown = open && dropRect
    ? ReactDOM.createPortal(
        <div
          className={styles.spDropdown}
          style={{ position: 'fixed', top: dropRect.top, left: dropRect.left, width: dropRect.width }}
          onMouseDown={e => e.stopPropagation()}
        >
          {tr(filtered.length === 0 && <div className={styles.spEmpty}>{tr("Ничего не найдено")}</div>)}
          {filtered.map(item => (
            <div
              key={item.value}
              className={`${styles.spItem} ${String(item.value) === String(value) ? styles.spItemActive : ''}`}
              onMouseDown={() => handleSelect(item)}
            >
              <div className={styles.spItemInfo}>
                <span className={styles.spItemLabel}>{tr(item.label)}</span>
                {tr(item.sub1 && <span className={styles.spItemSub1}>{tr(item.sub1)}</span>)}
                {tr(item.sub2 && <span className={styles.spItemSub2}>{tr(item.sub2)}</span>)}
              </div>
              {tr(String(item.value) === String(value) && <span className={styles.spItemCheck}>✓</span>)}
            </div>
          ))}
        </div>,
        document.body
      )
    : null

  return (
    <div className={styles.spWrap} ref={wrapRef}>
      {tr(label && (
        <label className={styles.spLabel}>
          {tr(label)}{tr(required && <span className={styles.req}> *</span>)}
        </label>
      ))}

      {/* Trigger — closed */}
      {!open ? (
        <div
          ref={triggerRef}
          className={`${styles.spTrigger} ${error ? styles.spTriggerErr : ''}`}
          onClick={handleOpen}
          role="button"
          tabIndex={0}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleOpen()}
        >
          {selected ? (
            <div className={styles.spSelected}>
              <div className={styles.spSelInfo}>
                <span className={styles.spSelLabel}>{tr(selected.label)}</span>
                {tr((selected.sub1 || selected.sub2) && (
                  <span className={styles.spSelMeta}>
                    {[selected.sub1, selected.sub2].filter(Boolean).join(' · ')}
                  </span>
                ))}
              </div>
              <button className={styles.spClear} onMouseDown={handleClear} tabIndex={-1}>×</button>
            </div>
          ) : (
            <span className={styles.spPlaceholder}>{tr(emptyLabel)}</span>
          )}
          <span className={styles.spChevron}>▾</span>
        </div>
      ) : (
        /* Trigger — open / search mode */
        <div ref={triggerRef} className={`${styles.spTrigger} ${styles.spTriggerOpen}`}>
          <span className={styles.spSearchIcon}>🔍</span>
          <input
            ref={inputRef}
            className={styles.spInput}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={tr(placeholder)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setOpen(false); setQuery('') }
              if (e.key === 'Enter' && filtered.length === 1) handleSelect(filtered[0])
            }}
          />
        </div>
      )}

      {tr(dropdown)}
      {tr(error && <span className={styles.err}>{tr(error)}</span>)}
    </div>
  )
}
